import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import moment from 'moment';
import { FilterMapping } from '../../../../ui-components/lib/filter-dropdown/filter-mapping.enum';
import { ApiResponse } from '../../models/api-response.model';
import { SearchEnum } from '../../enums/search.enum';
import { Insight } from '../../models/insight.model';
import { ENDPOINTS } from '../../constants/endpoints.constants';
import { ClosedRequest } from '../../models/closed-request.model';
import { InProgressRequest } from '../../models/inprogress-requests.model';
import { StepStatusEnum } from '../../enums/step-status';

@Injectable({
  providedIn: 'root',
})
export class MyOrganizationService {
  private readonly http = inject(HttpClient);

  private addFiltersToParams(
    params: HttpParams,
    filters?: Record<string, string | string[]>,
    useCurrentAssignees = false
  ): HttpParams {
    if (!filters || Object.keys(filters).length === 0) return params;

    // Parse resourceType JSON into separate filter params
    filters = this.expandResourceTypeFilter(filters, useCurrentAssignees);

    Object.entries(filters).forEach(([key, value]) => {
      if (!this.hasValidValue(value)) return;

      if (key === SearchEnum.keyword || key.includes('keyword')) return;

      const isNonIndexedArrayFilter = key.endsWith('][]');

      const isArrayFilter =
        !isNonIndexedArrayFilter &&
        (key.endsWith('][') ||
          key.includes('][ids][') ||
          key === FilterMapping.WORKFLOW_TYPES ||
          key === FilterMapping.WORKFLOW_TYPES_FLOWS ||
          key === FilterMapping.PROJECT_STATES);

      if (isNonIndexedArrayFilter) {
        const items = this.normalizeToArray(value);
        items.forEach(item => {
          if (item?.trim()) {
            params = params.append(`filters[${key}`, item.trim());
          }
        });
      } else if (isArrayFilter) {
        const items = this.normalizeToArray(value);
        items.forEach((item, index) => {
          if (item?.trim()) {
            params = params.append(`filters[${key}${index}]`, item.trim());
          }
        });
      } else if (typeof value === 'string') {
        const formattedValue = this.formatFilterValue(key, value.trim());
        const filterKey = key.endsWith(']')
          ? `filters[${key}`
          : `filters[${key}]`;
        params = params.append(filterKey, formattedValue);
      }
    });

    return params;
  }

  private expandResourceTypeFilter(
    filters: Record<string, string | string[]>,
    useCurrentAssignees = false
  ): Record<string, string | string[]> {
    const resourceTypeValue = filters['resourceType'];
    if (!resourceTypeValue || typeof resourceTypeValue !== 'string') {
      return filters;
    }

    const expanded = { ...filters };
    delete expanded['resourceType'];

    const typeKey = useCurrentAssignees
      ? FilterMapping.CURRENT_ASSIGNEES_TYPE
      : FilterMapping.ASSIGNABLE_TYPE_PLAIN;
    const idsKey = useCurrentAssignees
      ? FilterMapping.CURRENT_ASSIGNEES_IDS
      : FilterMapping.ASSIGNABLE_IDS_ARRAY;

    try {
      const parsed = JSON.parse(resourceTypeValue);
      if (parsed.resourceType) {
        expanded[typeKey] = parsed.resourceType;
      }
      if (parsed.assigneeId && parsed.assigneeId.length > 0) {
        expanded[idsKey] = Array.isArray(parsed.assigneeId)
          ? parsed.assigneeId.map(String)
          : [String(parsed.assigneeId)];
      }
    } catch {
      return filters;
    }

    return expanded;
  }

  private hasValidValue(value: string | string[] | null | undefined): boolean {
    if (!value) return false;
    if (Array.isArray(value)) return value.length > 0;
    return typeof value === 'string' && value.trim().length > 0;
  }

  private normalizeToArray(value: string | string[]): string[] {
    if (Array.isArray(value)) return value;
    return value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }

  private formatFilterValue(key: string, value: string): string {
    const isDateFilter =
      key.includes(FilterMapping.CREATED_BEFORE) ||
      key.includes(FilterMapping.CREATED_AFTER) ||
      key.includes(FilterMapping.CREATED_BEFORE_FLOWS) ||
      key.includes(FilterMapping.CREATED_AFTER_FLOWS) ||
      key === FilterMapping.CREATED_BEFORE_REQUESTS ||
      key === FilterMapping.CREATED_AFTER_REQUESTS ||
      key === FilterMapping.COMPLETED_BEFORE_REQUESTS ||
      key === FilterMapping.COMPLETED_AFTER_REQUESTS;

    const parsed = moment(value);
    if (!isDateFilter || !parsed.isValid()) {
      return value;
    }

    const [year, month, day] = [parsed.year(), parsed.month(), parsed.date()];
    const isAfter =
      key.includes(FilterMapping.CREATED_AFTER) ||
      key.includes(FilterMapping.CREATED_AFTER_FLOWS) ||
      key === FilterMapping.CREATED_AFTER_REQUESTS ||
      key === FilterMapping.COMPLETED_AFTER_REQUESTS;
    const [hour, minute, second] = isAfter ? [0, 0, 0] : [23, 59, 59];

    return moment
      .utc([year, month, day, hour, minute, second])
      .format('YYYY-MM-DD HH:mm:ss');
  }

  getClosedRequestsInsights() {
    return this.http.get<{ data: Insight[] }>(
      ENDPOINTS.MyOrganizationService.getClosedRequestsInsights
    );
  }

  getTabsInsight(): Observable<any> {
    return this.http.get(ENDPOINTS.MyOrganizationService.getTabsInsight);
  }

  getClosedRequestsList(
    page: number,
    per_page: number,
    filtersState: string[] | null,
    sort: { key: string; value: 'asc' | 'desc' },
    search: string,
    from: string | null,
    to: string | null,
    additionalFilters?: Record<string, string | string[]>
  ): Observable<any> {
    let params = new HttpParams()
      .append('page', page)
      .append('per_page', per_page);
    if (
      filtersState &&
      filtersState.length > 0 &&
      !additionalFilters?.[FilterMapping.PROJECT_STATES]
    ) {
      filtersState.forEach((state, index) => {
        params = params.append(`filters[states][${index}]`, state);
      });
    }
    if (from && to) {
      params = params
        .append('filters[created_after]', from)
        .append('filters[created_before]', to);
    }
    if (sort.key) {
      params = params.append(`sort[${sort.key}]`, sort.value);
    }
    if (search) {
      params = params.append(SearchEnum.keyword, search);
    }

    if (additionalFilters) {
      params = this.addFiltersToParams(params, additionalFilters, true);
    }

    return this.http.get<ApiResponse<ClosedRequest>>(
      ENDPOINTS.MyOrganizationService.getClosedRequestsList,
      { params }
    );
  }

  getInProgressInsights() {
    return this.http.get<{ data: Insight[] }>(
      ENDPOINTS.MyOrganizationService.getInProgressInsights
    );
  }

  getInProgressRequests(
    page: number,
    per_page: number,
    sort: { key: string; value: 'asc' | 'desc' },
    search: string,
    filter: string,
    additionalFilters?: Record<string, string | string[]>
  ): Observable<any> {
    let params = new HttpParams()
      .append('page', page)
      .append('per_page', per_page)
      .append('filters[finished_at]', '')
      .append('filters[step][state]', StepStatusEnum.INPROGRESS);
    if (filter === FilterMapping.EXPIRED_AT_BEFORE) {
      const currentDate = moment().utc().format('DD-MM-YYYY HH:mm:ss');
      params = params.append('filters[step][expired_at_before]', currentDate);
    }
    if (sort.key) {
      params = params.append(`sort[${sort.key}]`, sort.value);
    }
    if (search) {
      params = params.append(SearchEnum.keyword, search);
    }

    if (additionalFilters) {
      params = this.addFiltersToParams(params, additionalFilters);
    }
    return this.http.get<ApiResponse<InProgressRequest>>(
      ENDPOINTS.MyOrganizationService.getInProgressRequests,
      { params }
    );
  }
}
